import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendingUp, TrendingDown, Filter, Download, Edit2, Save, X } from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = ['Nouveau', 'Contacté', 'Qualifié', 'Gagné', 'Perdu'];
const STATUS_COLORS = {
  'Nouveau': 'bg-blue-500/20 text-blue-400',
  'Contacté': 'bg-purple-500/20 text-purple-400',
  'Qualifié': 'bg-orange-500/20 text-orange-400',
  'Gagné': 'bg-green-500/20 text-green-400',
  'Perdu': 'bg-red-500/20 text-red-400'
};

const DevisAnalytics = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    days: 30,
    status: '',
    type_beton: ''
  });
  const [editingLead, setEditingLead] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState('Nouveau');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Check auth
    if (!localStorage.getItem('adminAuth')) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch summary stats
      const statsResponse = await axios.get(
        `${BACKEND_URL}/api/devis/admin/stats?days=${filters.days}`
      );
      setStats(statsResponse.data);

      // Fetch detailed data
      const detailsResponse = await axios.get(
        `${BACKEND_URL}/api/devis/admin/details`, {
          params: {
            days: filters.days,
            status: filters.status || undefined,
            type_beton: filters.type_beton || undefined
          }
        }
      );
      setDetails(detailsResponse.data.data || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (lead) => {
    if (lead.status !== 'Lead') return; // Only leads can be edited
    // Always reset to the specific lead's current values
    setEditingLead(lead.id); // Use MongoDB _id instead of email
    setEditNotes(lead.admin_notes || '');
    setEditStatus(lead.admin_status || 'Nouveau');
  };

  const handleCancelEdit = () => {
    setEditingLead(null);
    setEditNotes('');
    setEditStatus('Nouveau');
  };

  const handleSaveNotes = async (leadId) => {
    try {
      setSaving(true);
      const response = await axios.patch(
        `${BACKEND_URL}/api/devis/admin/leads/${leadId}/notes`,
        {
          notes: editNotes,
          status: editStatus
        }
      );

      if (response.data.success) {
        // Update local state using MongoDB _id
        setDetails(prevDetails => 
          prevDetails.map(item => 
            item.id === leadId 
              ? { ...item, admin_notes: editNotes, admin_status: editStatus }
              : item
          )
        );
        setEditingLead(null);
      }
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Erreur lors de la sauvegarde des notes');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickTag = (tag) => {
    const timestamp = new Date().toLocaleString('fr-FR');
    const tagText = `[${timestamp}] ${tag}`;
    setEditNotes(prev => prev ? `${prev}\n${tagText}` : tagText);
  };

  const exportPDF = () => {
    if (!details.length) {
      alert('Aucune donnée à exporter');
      return;
    }

    try {
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(18);
      doc.setTextColor(201, 114, 73); // Terracotta color
      doc.text('Rapport Analytics - Calculateur Béton', 14, 20);
      
      // Add summary stats
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(`Période: ${filters.days} derniers jours`, 14, 30);
      if (stats?.summary) {
        doc.text(`Total Leads: ${stats.summary.total_leads} | Total Abandons: ${stats.summary.total_abandons}`, 14, 36);
        doc.text(`Taux de Conversion: ${stats.summary.conversion_rate}% | Valeur Moyenne: ${stats.summary.avg_total_leads}€`, 14, 42);
      }
      
      // Prepare table data
      const tableHeaders = [['Date', 'Type Béton', 'Surface (m²)', 'Total (€)', 'Status', 'Email', 'Statut Admin', 'Notes']];
      const tableData = details.map(d => [
        d.date,
        d.type_beton,
        d.surface,
        `${d.total_estime}€`,
        d.status,
        d.email || 'N/A',
        d.admin_status || '-',
        (d.admin_notes || '-').substring(0, 50) + (d.admin_notes?.length > 50 ? '...' : '')
      ]);
      
      // Add table
      autoTable(doc, {
        head: tableHeaders,
        body: tableData,
        startY: 50,
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [201, 114, 73], // Terracotta
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { cellWidth: 20 }, // Date
          1: { cellWidth: 25 }, // Type Béton
          2: { cellWidth: 20 }, // Surface
          3: { cellWidth: 20 }, // Total
          4: { cellWidth: 15 }, // Status
          5: { cellWidth: 35 }, // Email
          6: { cellWidth: 20 }, // Statut Admin
          7: { cellWidth: 35 }  // Notes
        },
        margin: { top: 50 }
      });
      
      // Add footer with date
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Généré le ${new Date().toLocaleDateString('fr-FR')} | Page ${i} sur ${pageCount}`,
          14,
          doc.internal.pageSize.height - 10
        );
      }
      
      // Save PDF
      doc.save(`calculator-analytics-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Erreur lors de la génération du PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-slate-300 text-xl">Loading analytics...</div>
      </div>
    );
  }

  const summary = stats?.summary || {};
  const conversionRate = summary.conversion_rate || 0;
  let conversionColor = 'text-red-400';
  if (conversionRate > 50) conversionColor = 'text-green-400';
  else if (conversionRate >= 30) conversionColor = 'text-orange-400';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-950 py-6 px-4 sm:px-6 lg:px-8 border-b border-slate-800">
        <div className="container mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="sm" className="text-slate-300 border-slate-600 hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#C97249] to-[#E97C30]">
                Calculator Analytics
              </h1>
              <p className="text-slate-400 mt-1">Concrete price calculator performance & conversion tracking</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400">Total Leads</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-400">{summary.total_leads}</div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400">Total Abandons</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-400">{summary.total_abandons}</div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400">Conversion Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${conversionColor} flex items-center gap-2`}>
                {conversionRate.toFixed(1)}%
                {conversionRate > 50 ? <TrendingUp className="w-6 h-6" /> : <TrendingDown className="w-6 h-6" />}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-slate-400">Avg Lead Value</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-100">{summary.avg_total_leads?.toFixed(0)}€</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-slate-800 border-slate-700 mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-slate-100 flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
              <Button onClick={exportPDF} variant="outline" size="sm" className="text-slate-300 border-slate-600">
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Period</label>
                <select
                  value={filters.days}
                  onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value) })}
                  className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded px-3 py-2"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                </select>
              </div>

              <div>
                <label className="text-sm text-slate-400 mb-2 block">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded px-3 py-2"
                >
                  <option value="">All</option>
                  <option value="lead">Leads Only</option>
                  <option value="abandon">Abandons Only</option>
                </select>
              </div>

              <div>
                <label className="text-sm text-slate-400 mb-2 block">Type Béton</label>
                <select
                  value={filters.type_beton}
                  onChange={(e) => setFilters({ ...filters, type_beton: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded px-3 py-2"
                >
                  <option value="">All Types</option>
                  <option value="Béton Imprimé">Béton Imprimé</option>
                  <option value="Béton Désactivé">Béton Désactivé</option>
                  <option value="Béton Bâlé">Béton Bâlé</option>
                  <option value="Béton Lissé">Béton Lissé</option>
                  <option value="Béton Ciré">Béton Ciré</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Table */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-slate-100">Detailed Interactions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Type Béton</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-300">Surface (m²)</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-300">Total (€)</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-300">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Statut / Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {details.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-slate-400">
                        No data available for the selected filters
                      </td>
                    </tr>
                  ) : (
                    details.map((item, index) => (
                      <tr key={item.id || `${item.email}-${index}`} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-3 px-4 text-sm text-slate-300">{item.date}</td>
                        <td className="py-3 px-4 text-sm text-slate-300">{item.type_beton}</td>
                        <td className="py-3 px-4 text-sm text-slate-300 text-right">{item.surface}</td>
                        <td className="py-3 px-4 text-sm text-slate-100 font-semibold text-right">{item.total_estime}€</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            item.status === 'Lead' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-400">
                          {item.email || <span className="text-slate-600">—</span>}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {item.status === 'Lead' ? (
                            editingLead === item.id ? (
                              <div className="space-y-2 min-w-[300px]">
                                {/* Status selector */}
                                <div className="flex gap-1 flex-wrap">
                                  {STATUS_OPTIONS.map(status => (
                                    <button
                                      key={status}
                                      onClick={() => setEditStatus(status)}
                                      className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${
                                        editStatus === status 
                                          ? STATUS_COLORS[status]
                                          : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                                      }`}
                                    >
                                      {status}
                                    </button>
                                  ))}
                                </div>
                                
                                {/* Notes textarea */}
                                <textarea
                                  value={editNotes}
                                  onChange={(e) => setEditNotes(e.target.value)}
                                  placeholder="Ajouter des notes..."
                                  className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded px-2 py-1 text-sm"
                                  rows={3}
                                />
                                
                                {/* Action buttons */}
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => handleSaveNotes(item.id)}
                                    disabled={saving}
                                    size="sm"
                                    className="bg-[#C97249] hover:bg-[#B86239] text-white"
                                  >
                                    <Save className="w-3 h-3 mr-1" />
                                    {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                                  </Button>
                                  <Button
                                    onClick={handleCancelEdit}
                                    disabled={saving}
                                    size="sm"
                                    variant="outline"
                                    className="border-slate-600 text-slate-300"
                                  >
                                    <X className="w-3 h-3 mr-1" />
                                    Annuler
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                {/* Display status badge */}
                                {item.admin_status && (
                                  <div>
                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${STATUS_COLORS[item.admin_status] || 'bg-slate-700 text-slate-400'}`}>
                                      {item.admin_status}
                                    </span>
                                  </div>
                                )}
                                
                                {/* Display notes */}
                                {item.admin_notes ? (
                                  <div className="text-slate-400 text-xs whitespace-pre-wrap max-w-[300px]">
                                    {item.admin_notes}
                                  </div>
                                ) : (
                                  <div className="text-slate-600 text-xs italic">
                                    Aucune note
                                  </div>
                                )}
                                
                                {/* Edit button */}
                                <button
                                  onClick={() => handleEditClick(item)}
                                  className="text-[#C97249] hover:text-[#E97C30] text-xs flex items-center gap-1 mt-1"
                                >
                                  <Edit2 className="w-3 h-3" />
                                  Modifier
                                </button>
                              </div>
                            )
                          ) : (
                            <span className="text-slate-600 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {details.length > 0 && (
              <div className="mt-4 text-sm text-slate-400 text-center">
                Showing {details.length} interactions
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Types Analysis */}
        {stats?.top_types_leads && (
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-slate-100">Top Types - Leads</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.top_types_leads.slice(0, 5).map((item, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-slate-300">{item.type}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-slate-700 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full" 
                            style={{ width: `${(item.count / stats.summary.total_leads) * 100}%` }}
                          />
                        </div>
                        <span className="text-green-400 font-semibold min-w-[2rem] text-right">{item.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-slate-100">Top Types - Abandons</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.top_types_abandons.slice(0, 5).map((item, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-slate-300">{item.type}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-slate-700 rounded-full h-2">
                          <div 
                            className="bg-red-500 h-2 rounded-full" 
                            style={{ width: `${(item.count / stats.summary.total_abandons) * 100}%` }}
                          />
                        </div>
                        <span className="text-red-400 font-semibold min-w-[2rem] text-right">{item.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default DevisAnalytics;
